import { DOH, FIELD, VIEW } from '../core/config.js';
import { createDohShot } from './pickups.js';

/**
 * DOH, the Moai-faced guardian of the final round.
 *
 * Phases:
 *   entering  drops in from above the ceiling, invulnerable
 *   active    drifts side to side, bobs, and spits orbs at Vaus
 *   dying     locked in place while the explosion sequence plays
 */

export const DOH_PHASE = Object.freeze({
  ENTERING: 'entering',
  ACTIVE: 'active',
  DYING: 'dying',
});

export function createDoh() {
  return {
    w: DOH.w,
    h: DOH.h,
    cx: VIEW.W / 2,
    y: -DOH.h,
    hp: DOH.hp,
    maxHp: DOH.hp,
    phase: DOH_PHASE.ENTERING,
    dir: Math.random() < 0.5 ? -1 : 1,
    /** Damage cooldown so one ball pass cannot chew through the health bar. */
    iframe: 0,
    hitFlash: 0,
    /** 0 closed, 1 fully open. */
    mouth: 0,
    shotTimer: DOH.shotInterval * 0.6,
    bob: 0,
    deathTimer: 0,
  };
}

export function dohBounds(doh) {
  return {
    left: doh.cx - doh.w / 2,
    right: doh.cx + doh.w / 2,
    top: doh.y,
    bottom: doh.y + doh.h,
  };
}

/**
 * @param {object} doh
 * @param {number} dt
 * @param {{paddleCx: number, shots: object[], onShoot: Function, rage: number}} ctx
 */
export function updateDoh(doh, dt, ctx) {
  doh.hitFlash = Math.max(0, doh.hitFlash - dt * 3.2);
  doh.iframe = Math.max(0, doh.iframe - dt);

  if (doh.phase === DOH_PHASE.ENTERING) {
    doh.y += 120 * dt;
    if (doh.y >= 132) {
      doh.y = 132;
      doh.phase = DOH_PHASE.ACTIVE;
    }
    return;
  }

  if (doh.phase === DOH_PHASE.DYING) {
    doh.deathTimer += dt;
    doh.mouth = Math.min(1, doh.mouth + dt * 1.6);
    return;
  }

  // Rage rises as health drops: faster drifting and more frequent volleys.
  const rage = 1 + (1 - doh.hp / doh.maxHp) * 1.2;
  doh.cx += DOH.driftSpeed * rage * doh.dir * dt;
  const half = doh.w / 2;
  if (doh.cx - half <= FIELD.left + 6) {
    doh.cx = FIELD.left + 6 + half;
    doh.dir = 1;
  } else if (doh.cx + half >= FIELD.right - 6) {
    doh.cx = FIELD.right - 6 - half;
    doh.dir = -1;
  }

  doh.bob += dt * 1.1;
  doh.y = 132 + Math.sin(doh.bob) * 14;

  doh.shotTimer -= dt * rage;
  if (doh.shotTimer <= 0) {
    doh.shotTimer = DOH.shotInterval;
    fireAtPaddle(doh, ctx);
  }

  // Mouth opens just before a shot and snaps shut afterwards.
  const windup = doh.shotTimer < 0.45 ? 1 : 0;
  doh.mouth += ((windup ? 1 : 0) - doh.mouth) * Math.min(1, dt * 7);
}

function fireAtPaddle(doh, ctx) {
  const originX = doh.cx;
  const originY = doh.y + doh.h * 0.72;
  const dx = ctx.paddleCx - originX;
  const dy = 592 - originY;
  const len = Math.hypot(dx, dy) || 1;
  const spread = doh.hp / doh.maxHp < 0.5 ? [-0.22, 0, 0.22] : [0];

  for (const offset of spread) {
    const angle = Math.atan2(dy / len, dx / len) + offset;
    ctx.shots.push(
      createDohShot(
        originX,
        originY,
        Math.cos(angle) * DOH.shotSpeed,
        Math.sin(angle) * DOH.shotSpeed,
        DOH.shotSize,
      ),
    );
  }
  ctx.onShoot?.();
}

/**
 * @returns {'immune'|'hit'|'killed'}
 */
export function damageDoh(doh, amount = 1) {
  if (doh.phase !== DOH_PHASE.ACTIVE || doh.iframe > 0) return 'immune';
  doh.hp -= amount;
  doh.iframe = DOH.iframe;
  doh.hitFlash = 1;
  if (doh.hp <= 0) {
    doh.hp = 0;
    doh.phase = DOH_PHASE.DYING;
    doh.deathTimer = 0;
    return 'killed';
  }
  return 'hit';
}
